<template>
  <div class="executive-summary-impact-card pa-6" data-testid="executive-summary-impact-card">
    <div class="d-flex align-center justify-space-between mb-3">
      <div class="d-flex align-center ga-2">
        <v-icon size="40" :icon="moduleIcon" :class="{ 'text-indigo-medium': disabled }" />
        <span
          class="subtitle1"
          :class="{ 'text-indigo-medium': disabled }"
          data-testid="executive-summary-impact-card-title"
        >
          {{ $t(`reports.executiveSummaryReport.modules.${module}.title`) }}
        </span>
      </div>
      <!-- Styled from the theme's yellow pair rather than `color="warning" variant="tonal"`:
           tonal paints a 12%-opacity underlay in Vuetify's own orange, which read as a nearly
           transparent pill next to the mock's solid faint-yellow one. -->
      <v-chip
        v-if="disabled"
        class="executive-summary-impact-card__chip"
        label
        variant="flat"
        density="compact"
        data-testid="executive-summary-impact-card-chip"
      >
        <span class="coro-label text-uppercase">
          {{ $t("reports.executiveSummaryReport.disabledChip") }}
        </span>
      </v-chip>
    </div>
    <!-- A subscribed module always gets the sentence, even with nothing to report: absent numbers
         are interpolated as zeros so it reads "Blocked 0 spam messages…" instead of dropping to
         the placeholder copy, which is reserved for a module that is not subscribed at all. -->
    <div
      v-if="!disabled"
      class="body2"
      data-testid="executive-summary-impact-card-narrative"
      v-html="narrative"
    ></div>
    <template v-else>
      <div class="body2 text-indigo-medium" data-testid="executive-summary-impact-card-short">
        {{ $t(`reports.executiveSummaryReport.modules.${module}.shortDescription`) }}
      </div>
      <!-- `text-link` rather than the `coro-link` class used elsewhere: that one is defined
           inside another component's scoped block, so its rule carries a foreign `data-v-` hash
           and would never match this anchor. -->
      <a
        class="executive-summary-impact-card__learn-more body2 text-link d-inline-flex align-center mt-2"
        :href="CoroMarketingLink.PLATFORM_MODULES"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="executive-summary-impact-card-learn-more"
      >
        {{ $t("general.learnMore") }}
        <v-icon icon="$chevronRight" size="16" />
      </a>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { REPORT_MODULE_ICONS_S } from "@/constants/reports";
import { formatReportNumber, formatReportPercentValue } from "@/_helpers/reports";
import { SubscriptionModule } from "@/constants/workplaces";
import type { TicketGeneratingSubscriptionModule } from "@/constants/workplaces";
import { CoroMarketingLink } from "@/constants/general";
import type {
  ExecutiveSummaryImpactByModule,
  ExecutiveSummaryModuleImpact,
} from "@/_store/reports.module";

/**
 * Formats an impact payload for interpolation into the narrative sentence.
 * Counts gain thousands separators and percentages are capped at two decimals, so a
 * narrative never renders a bare `7259` beside the KPI strip's `9,554`, nor a raw `77.9873%`.
 * Truncating a whole number is a no-op, which is why one pass covers both kinds of value.
 * Counts and percentages are formatted differently — counts take the locale's thousands
 * separators, percentages keep a `.` decimal separator so one report never mixes "77,99%" with
 * "77.99%" — so the key name decides which formatter applies.
 * @param impact - The module's impact numbers, as they arrive in the response.
 * @returns The same keys with display-ready string values.
 */
const formatImpactValues = (impact: Record<string, number>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(impact).map(([key, value]) => [
      key,
      key.endsWith("Percent") ? formatReportPercentValue(value) : formatReportNumber(value),
    ])
  );

/**
 * Every impact field of every module at zero.
 *
 * Written out as the complete map rather than derived, so adding a field to any impact shape is
 * a compile error here until it gets a zero. That matters because the narrative interpolates
 * NAMED placeholders: one the object does not carry is not rendered as an empty string, it is
 * left in the sentence as the literal `{name}`.
 */
const ZERO_IMPACT: ExecutiveSummaryImpactByModule = {
  [SubscriptionModule.CLOUD_SECURITY]: { threats: 0 },
  [SubscriptionModule.ENDPOINT_SECURITY]: { risks: 0, autoResolvedPercent: 0 },
  [SubscriptionModule.EMAIL_SECURITY]: {
    spamBlocked: 0,
    additionalThreats: 0,
    autoResolvedPercent: 0,
  },
  [SubscriptionModule.USER_DATA_GOVERNANCE]: { tickets: 0, healthData: 0, creditCardData: 0 },
  [SubscriptionModule.ENDPOINT_DATA_GOVERNANCE]: { tickets: 0, healthData: 0, creditCardData: 0 },
  [SubscriptionModule.EDR]: { indicators: 0 },
};

/**
 * Overlays whatever numbers the payload carries on that module's all-zero defaults.
 *
 * Covers both shapes of "no impact" the backend can send: the module's key missing from
 * `securityImpact` entirely, and the key present with fields empty. Non-numeric values are
 * skipped rather than passed through, so a `null` field keeps its zero instead of interpolating
 * the word "null" into the sentence.
 * @param module - The module whose impact sentence is being built.
 * @param impact - The payload's impact for that module, if it sent one.
 * @returns Every field the module's narrative can interpolate, none of them missing.
 */
const withZeroDefaults = (
  module: TicketGeneratingSubscriptionModule,
  impact?: ExecutiveSummaryModuleImpact
): Record<string, number> => {
  const merged: Record<string, number> = { ...ZERO_IMPACT[module] };
  for (const [key, value] of Object.entries(impact ?? {})) {
    if (typeof value === "number") {
      merged[key] = value;
    }
  }
  return merged;
};

export default defineComponent({
  props: {
    module: {
      type: String as PropType<TicketGeneratingSubscriptionModule>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    impact: {
      type: Object as PropType<ExecutiveSummaryModuleImpact>,
      default: undefined,
    },
  },
  setup(props) {
    const i18n = useI18n();

    const moduleIcon = computed(() => REPORT_MODULE_ICONS_S[props.module]);

    const narrative = computed(() =>
      i18n.t(
        `reports.executiveSummaryReport.modules.${props.module}.impact`,
        formatImpactValues(withZeroDefaults(props.module, props.impact))
      )
    );

    return { moduleIcon, narrative, CoroMarketingLink };
  },
});
</script>

<style scoped lang="scss">
.executive-summary-impact-card {
  border: 1px solid rgb(var(--v-theme-indigo-faint));
  border-radius: 8px;
  height: 100%;

  // The mock names these two by token — Warning/Yellow Faint behind Warning/Yellow Base — so they
  // are taken from the theme's pair rather than matched to the mock's hex, which has drifted from
  // it on the base colour.
  &__chip {
    background-color: rgb(var(--v-theme-yellow-faint));
    color: rgb(var(--v-theme-yellow-base));
    border-radius: 4px;
    padding: 4px 8px;
  }

  &__learn-more {
    text-decoration: none;
    gap: 2px;

    .v-icon,
    .v-icon::before {
      color: inherit;
    }
  }

  .v-icon.text-indigo-medium::before {
    color: inherit;
  }
}
</style>
